/**
 * Skills Marketplace View — Browse, install, create, review, and vote on skills.
 *
 * Skills are prompt + tool bundles that extend the assistant's capabilities.
 * Users can browse the catalog, install skills, create their own community
 * skills, and leave reviews/votes.
 */

import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, state, query } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { marked } from "marked";
import DOMPurify from "dompurify";
import {
  directRestRequest,
  SUPABASE_PROJECT_URL,
  getAccessTokenDirect,
  getSessionDirect,
  SUPABASE_KEY,
} from "../services/supabase-client.ts";

// ── Types ──

type SkillCategory =
  | "all"
  | "personal"
  | "productivity"
  | "developer"
  | "communication"
  | "data"
  | "creative"
  | "other";

type SettingsSchemaEntry = {
  key: string;
  label: string;
  type: "secret" | "string" | "boolean" | "select";
  required?: boolean;
  placeholder?: string;
  help_url?: string;
  options?: string[];
};

type SkillCommand = {
  name: string;
  description: string;
  prepend_prompt: string;
};

type Skill = {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  emoji: string | null;
  author: string;
  version: string;
  required_tools: string[];
  http_actions: unknown[];
  settings_schema: SettingsSchemaEntry[];
  commands: SkillCommand[];
  pricing: string;
  // Marketplace fields
  user_id?: string | null;
  is_bundled?: boolean;
  install_count?: number;
  vote_score?: number;
  average_rating?: number;
  review_count?: number;
};

type UserSkill = {
  id: string;
  skill_id: string;
  enabled: boolean;
  installed_at: string;
};

type InstalledSkillView = {
  userSkill: UserSkill;
  skill: Skill;
};

type SkillReview = {
  id: string;
  user_id: string;
  rating: number;
  title: string | null;
  body: string | null;
  created_at: string;
};

type SkillVote = {
  skill_id: string;
  vote: number;
};

type SkillHttpAction = {
  id: string;
  name: string;
  description: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  url_template: string;
  headers?: Record<string, string>;
  params?: Record<string, string>;
  settings_keys?: string[];
  input_params?: Array<{ name: string; type: string; description: string; required?: boolean }>;
  response_path?: string;
  timeout_ms?: number;
};

type CreateTab = "general" | "prompt" | "actions" | "settings" | "advanced";

type SortOption = "popular" | "top_rated" | "newest" | "name";
type FilterTab = "all" | "official" | "community" | "my_skills";

// ── Category labels ──

const CATEGORIES: { id: SkillCategory; label: string }[] = [
  { id: "all", label: "All" },
  { id: "productivity", label: "Productivity" },
  { id: "developer", label: "Developer" },
  { id: "data", label: "Data" },
  { id: "creative", label: "Creative" },
  { id: "communication", label: "Communication" },
  { id: "personal", label: "Personal" },
  { id: "other", label: "Other" },
];

const SORT_OPTIONS: { id: SortOption; label: string }[] = [
  { id: "popular", label: "Most Popular" },
  { id: "top_rated", label: "Top Rated" },
  { id: "newest", label: "Newest" },
  { id: "name", label: "A-Z" },
];

const FILTER_TABS: { id: FilterTab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "official", label: "Official" },
  { id: "community", label: "Community" },
  { id: "my_skills", label: "My Skills" },
];

// ── Skill Icons (stroke SVGs matching the app design system) ──

const si = `viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"`;

const SKILL_ICONS: Record<string, string> = {
  weather: `<svg ${si}><path d="M17.5 19H9.5A7 7 0 0 1 7 5.2a5 5 0 0 1 9.9 1.1A4.5 4.5 0 1 1 17.5 19z"/></svg>`,
  "github-summary": `<svg ${si}><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>`,
  "email-drafter": `<svg ${si}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`,
  "coding-tutor": `<svg ${si}><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/><line x1="14" y1="4" x2="10" y2="20"/></svg>`,
  translator: `<svg ${si}><path d="M5 8l6 6"/><path d="M4 14l6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/></svg>`,
  "daily-digest": `<svg ${si}><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8z"/></svg>`,
  "writing-coach": `<svg ${si}><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>`,
  "recipe-finder": `<svg ${si}><path d="M15 11h.01"/><path d="M11 15h.01"/><path d="M16 16h.01"/><path d="m2 16 20 6-6-20A20 20 0 0 0 2 16"/><path d="M5.71 17.11a17.04 17.04 0 0 1 11.4-11.4"/></svg>`,
  "meeting-notes": `<svg ${si}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M9 12h6"/><path d="M9 16h6"/></svg>`,
  "hacker-news": `<svg ${si}><path d="M2 3h6.5l3.5 7 3.5-7H22"/><line x1="12" y1="10" x2="12" y2="21"/></svg>`,
};

/** Default icon for skills that don't match a known slug */
const DEFAULT_SKILL_ICON = `<svg ${si}><circle cx="12" cy="12" r="10"/><path d="m8 12 3 3 5-5"/></svg>`;

/** Render a skill icon — prefers the emoji field (icon key) then slug lookup */
function renderSkillIcon(skill: Skill): TemplateResult {
  const svg = (skill.emoji && SKILL_ICONS[skill.emoji]) || SKILL_ICONS[skill.slug] || DEFAULT_SKILL_ICON;
  return html`<div class="skill-card__icon">${unsafeHTML(svg)}</div>`;
}

/** Render star rating display */
function renderStars(rating: number): TemplateResult {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    const fill = i <= Math.round(rating) ? "var(--yellow, #eab308)" : "none";
    const stroke = i <= Math.round(rating) ? "var(--yellow, #eab308)" : "var(--text-tertiary)";
    stars.push(html`<svg width="14" height="14" viewBox="0 0 24 24" fill="${fill}" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`);
  }
  return html`<span class="skill-stars">${stars}</span>`;
}

/** Slugify a name: lowercase, replace spaces/special chars with hyphens */
function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 50);
}

// ── Dev mode check ──
const IS_DEV_MODE = import.meta.env.VITE_DEV_MODE === "true";

// ── Markdown rendering pipeline (same config as chat.ts) ──
marked.setOptions({ gfm: true, breaks: true });

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const SKILL_PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "em", "del", "code", "pre",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li", "blockquote",
    "table", "thead", "tbody", "tr", "th", "td",
    "hr", "a", "img", "span",
  ],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "class", "target", "rel"],
};

function renderSkillMarkdown(content: string): string {
  const raw = marked.parse(content) as string;
  return DOMPurify.sanitize(raw, SKILL_PURIFY_CONFIG);
}

// ── Available tools for the required_tools selector ──
const AVAILABLE_TOOLS = [
  { id: "web_search", label: "Web Search", desc: "Search the web for current information" },
  { id: "web_fetch", label: "Web Fetch", desc: "Fetch and read URL contents" },
  { id: "image_analyze", label: "Image Analyze", desc: "AI vision analysis" },
  { id: "tts", label: "Text to Speech", desc: "Convert text to audio" },
  { id: "memory_search", label: "Memory Search", desc: "Search user long-term memory" },
  { id: "memory_save", label: "Memory Save", desc: "Save facts to long-term memory" },
];

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;

// ── Mock data for dev mode ──
const MOCK_SKILLS: Skill[] = [
  { id: "m1", slug: "weather", name: "Weather", description: "Get current weather conditions and forecasts for any location worldwide.", category: "data", tags: ["weather"], emoji: null, author: "katanoid", version: "1.0.0", required_tools: [], http_actions: [{ id: "get_weather" }], settings_schema: [{ key: "WEATHER_API_KEY", label: "WeatherAPI Key", type: "secret" as const, required: true, placeholder: "Enter your WeatherAPI key", help_url: "https://www.weatherapi.com/signup.aspx" }], commands: [{ name: "weather", description: "Check the weather for a location", prepend_prompt: "Check the current weather for: " }], pricing: "free", is_bundled: true, install_count: 42, vote_score: 12, average_rating: 4.5, review_count: 8 },
  { id: "m2", slug: "github-summary", name: "GitHub Summary", description: "Summarize GitHub pull requests, issues, and repositories from URLs.", category: "developer", tags: ["github"], emoji: null, author: "katanoid", version: "1.0.0", required_tools: ["web_fetch"], http_actions: [], settings_schema: [], commands: [{ name: "github", description: "Summarize a GitHub PR, issue, or repo", prepend_prompt: "Summarize this GitHub page: " }], pricing: "free", is_bundled: true, install_count: 30, vote_score: 8, average_rating: 4.0, review_count: 5 },
  { id: "m3", slug: "email-drafter", name: "Email Drafter", description: "Draft professional, well-structured emails for any occasion.", category: "productivity", tags: ["email"], emoji: null, author: "katanoid", version: "1.0.0", required_tools: [], http_actions: [], settings_schema: [], commands: [{ name: "email", description: "Draft a professional email", prepend_prompt: "Help me draft an email: " }], pricing: "free", is_bundled: true, install_count: 55, vote_score: 15, average_rating: 4.8, review_count: 12 },
  { id: "m4", slug: "coding-tutor", name: "Coding Tutor", description: "Learn programming concepts with clear explanations and examples.", category: "developer", tags: ["coding"], emoji: null, author: "katanoid", version: "1.0.0", required_tools: [], http_actions: [], settings_schema: [], commands: [{ name: "teach", description: "Learn about a programming concept", prepend_prompt: "Teach me about: " }], pricing: "free", is_bundled: true, install_count: 20, vote_score: 6, average_rating: 4.2, review_count: 3 },
  { id: "m5", slug: "translator", name: "Translator", description: "Translate text between languages with natural, context-aware results.", category: "productivity", tags: ["translate"], emoji: null, author: "katanoid", version: "1.0.0", required_tools: [], http_actions: [], settings_schema: [], commands: [{ name: "translate", description: "Translate text to another language", prepend_prompt: "Translate the following: " }], pricing: "free", is_bundled: true, install_count: 35, vote_score: 10, average_rating: 4.3, review_count: 7 },
  { id: "m6", slug: "daily-digest", name: "Daily Digest", description: "Get a personalized daily news briefing based on your interests.", category: "productivity", tags: ["news"], emoji: null, author: "katanoid", version: "1.0.0", required_tools: ["web_search", "memory_search"], http_actions: [], settings_schema: [], commands: [{ name: "digest", description: "Get your personalized daily news briefing", prepend_prompt: "Give me my daily news digest. " }], pricing: "free", is_bundled: true, install_count: 18, vote_score: 4, average_rating: 3.8, review_count: 2 },
  { id: "m7", slug: "writing-coach", name: "Writing Coach", description: "Improve your writing with style suggestions, clarity edits, and feedback.", category: "creative", tags: ["writing"], emoji: null, author: "katanoid", version: "1.0.0", required_tools: [], http_actions: [], settings_schema: [], commands: [{ name: "review", description: "Get feedback on your writing", prepend_prompt: "Review and improve this writing: " }], pricing: "free", is_bundled: true, install_count: 22, vote_score: 7, average_rating: 4.1, review_count: 4 },
  { id: "m8", slug: "recipe-finder", name: "Recipe Finder", description: "Find recipes based on ingredients you have, dietary needs, or cuisines.", category: "personal", tags: ["recipe"], emoji: null, author: "katanoid", version: "1.0.0", required_tools: ["web_search"], http_actions: [], settings_schema: [], commands: [{ name: "recipe", description: "Find a recipe based on ingredients or cuisine", prepend_prompt: "Find me a recipe for: " }], pricing: "free", is_bundled: true, install_count: 25, vote_score: 9, average_rating: 4.4, review_count: 6 },
  { id: "m9", slug: "meeting-notes", name: "Meeting Notes", description: "Structure messy meeting notes into organized summaries with action items.", category: "productivity", tags: ["meeting"], emoji: null, author: "katanoid", version: "1.0.0", required_tools: [], http_actions: [], settings_schema: [], commands: [{ name: "notes", description: "Structure meeting notes with action items", prepend_prompt: "Organize these meeting notes into a structured summary: " }], pricing: "free", is_bundled: true, install_count: 15, vote_score: 5, average_rating: 4.0, review_count: 3 },
  { id: "m10", slug: "hacker-news", name: "Hacker News", description: "Browse and summarize top stories from Hacker News.", category: "data", tags: ["tech news"], emoji: null, author: "katanoid", version: "1.0.0", required_tools: ["web_fetch"], http_actions: [], settings_schema: [], commands: [{ name: "hn", description: "Browse top Hacker News stories", prepend_prompt: "Show me the top stories from Hacker News right now. " }], pricing: "free", is_bundled: true, install_count: 28, vote_score: 11, average_rating: 4.6, review_count: 9 },
];

// ── Helper: call skills-manage edge function ──
async function callSkillsManage(body: Record<string, unknown>): Promise<Response> {
  const token = getAccessTokenDirect();
  return fetch(`${SUPABASE_PROJECT_URL}/functions/v1/skills-manage`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

// ── Component ──

@customElement("katanoid-skills-view")
export class KatanoidSkillsView extends LitElement {
  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  // ── Core state ──
  @state() private catalog: Skill[] = [];
  @state() private installed: InstalledSkillView[] = [];
  @state() private loading = true;
  @state() private category: SkillCategory = "all";
  @state() private search = "";
  @state() private sortBy: SortOption = "popular";
  @state() private filterTab: FilterTab = "all";
  @state() private expandedSkillId: string | null = null;
  @state() private settingsValues: Record<string, string> = {};
  @state() private settingsSaving = false;
  @state() private settingsHints: Record<string, { hasValue: boolean; hint?: string }> = {};
  @state() private actionError: string | null = null;

  // ── Setup wizard state ──
  @state() private setupWizardSkill: Skill | null = null;
  @state() private setupWizardValues: Record<string, string> = {};
  @state() private setupWizardInstalling = false;
  @state() private setupWizardError: string | null = null;

  // ── Create skill modal state ──
  @state() private showCreateModal = false;
  @state() private createTab: CreateTab = "general";
  @state() private createForm = {
    name: "", slug: "", description: "", category: "other" as string,
    prompt: "", tags: "", emoji: "",
    required_tools: [] as string[],
  };
  @state() private createCommands: SkillCommand[] = [];
  @state() private createHttpActions: SkillHttpAction[] = [];
  @state() private createSettingsSchema: SettingsSchemaEntry[] = [];
  @state() private createSaving = false;
  @state() private createError: string | null = null;
  @state() private editingSkillId: string | null = null; // non-null = editing
  @state() private expandedActionIdx: number | null = null;
  @state() private expandedActionSections: Record<string, boolean> = {};
  @state() private expandedSchemaAdvanced: Record<number, boolean> = {};
  @state() private editingSlug = false;
  @state() private showIconPicker = false;

  @query(".skill-prompt-textarea") private promptTextarea!: HTMLTextAreaElement;

  // ── Detail / review state ──
  @state() private detailSkill: Skill | null = null;
  @state() private detailReviews: SkillReview[] = [];
  @state() private detailReviewsTotal = 0;
  @state() private detailReviewsPage = 1;
  @state() private detailReviewsLoading = false;
  @state() private userVotes: Map<string, number> = new Map();

  // ── Review form state ──
  @state() private reviewRating = 0;
  @state() private reviewTitle = "";
  @state() private reviewBody = "";
  @state() private reviewSaving = false;

  private get currentUserId(): string | undefined {
    return getSessionDirect()?.userId;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.loadData();
  }

  private async loadData(): Promise<void> {
    this.loading = true;
    try {
      if (IS_DEV_MODE) {
        this.catalog = MOCK_SKILLS;
        this.installed = [];
      } else {
        await Promise.all([this.loadCatalog(), this.loadInstalled(), this.loadUserVotes()]);
      }
    } finally {
      this.loading = false;
    }
  }

  private async loadCatalog(): Promise<void> {
    // Load all public skills + user's own unpublished skills
    const resp = await directRestRequest(
      "/skills?or=(is_public.eq.true,user_id.eq." + (this.currentUserId ?? "00000000-0000-0000-0000-000000000000") + ")&order=install_count.desc.nullsfirst,name.asc"
    );
    if (resp.ok) {
      this.catalog = await resp.json();
    }
  }

  private async loadInstalled(): Promise<void> {
    const resp = await directRestRequest(
      "/user_skills?select=id,skill_id,enabled,installed_at,skills(*)"
    );
    if (resp.ok) {
      const rows = await resp.json() as Array<UserSkill & { skills: Skill | null }>;
      this.installed = rows
        .filter((r) => r.skills != null)
        .map((r) => ({
          userSkill: { id: r.id, skill_id: r.skill_id, enabled: r.enabled, installed_at: r.installed_at },
          skill: r.skills!,
        }));
    }
  }

  private async loadUserVotes(): Promise<void> {
    const userId = this.currentUserId;
    if (!userId) return;
    const resp = await directRestRequest(
      `/skill_votes?user_id=eq.${userId}&select=skill_id,vote`
    );
    if (resp.ok) {
      const rows = await resp.json() as SkillVote[];
      this.userVotes = new Map(rows.map((r) => [r.skill_id, r.vote]));
    }
  }

  /** Get installed skill views */
  private getInstalledViews(): InstalledSkillView[] {
    return this.installed.filter((iv) => iv.skill);
  }

  /** Get skills for the catalog, filtered and sorted */
  private getAvailableSkills(): Skill[] {
    const installedIds = new Set(this.installed.map((iv) => iv.userSkill.skill_id));
    const userId = this.currentUserId;
    let skills = this.catalog.filter((s) => !installedIds.has(s.id));

    // Filter tab
    if (this.filterTab === "official") {
      skills = skills.filter((s) => s.is_bundled);
    } else if (this.filterTab === "community") {
      skills = skills.filter((s) => !s.is_bundled);
    } else if (this.filterTab === "my_skills") {
      skills = this.catalog.filter((s) => s.user_id === userId);
    }

    // Category filter
    if (this.category !== "all") {
      skills = skills.filter((s) => s.category === this.category);
    }

    // Search filter
    if (this.search.trim()) {
      const q = this.search.toLowerCase();
      skills = skills.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          s.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    // Sort
    switch (this.sortBy) {
      case "popular":
        skills.sort((a, b) => (b.install_count ?? 0) - (a.install_count ?? 0));
        break;
      case "top_rated":
        skills.sort((a, b) => (b.average_rating ?? 0) - (a.average_rating ?? 0));
        break;
      case "newest":
        skills.sort((a, b) => (b.id > a.id ? 1 : -1));
        break;
      case "name":
        skills.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return skills;
  }

  // ══════════════════════════════════════════
  // Actions (existing)
  // ══════════════════════════════════════════

  private installSkill(skillId: string): void {
    this.actionError = null;
    const skill = this.catalog.find((s) => s.id === skillId);
    if (!skill) return;

    const hasSettings = skill.settings_schema && skill.settings_schema.length > 0;
    if (hasSettings) {
      this.setupWizardSkill = skill;
      this.setupWizardValues = {};
      this.setupWizardError = null;
      this.setupWizardInstalling = false;
      return;
    }

    this.doInstallSkill(skill, {});
  }

  private closeSetupWizard(): void {
    this.setupWizardSkill = null;
    this.setupWizardValues = {};
    this.setupWizardError = null;
    this.setupWizardInstalling = false;
  }

  private async installFromWizard(): Promise<void> {
    const skill = this.setupWizardSkill;
    if (!skill) return;

    const missing = skill.settings_schema
      .filter((s) => s.required && !this.setupWizardValues[s.key]?.trim())
      .map((s) => s.label);
    if (missing.length > 0) {
      this.setupWizardError = `Please fill in: ${missing.join(", ")}`;
      return;
    }

    this.setupWizardInstalling = true;
    this.setupWizardError = null;

    await this.doInstallSkill(skill, this.setupWizardValues);

    if (!this.actionError) {
      this.closeSetupWizard();
    } else {
      this.setupWizardError = this.actionError;
      this.actionError = null;
      this.setupWizardInstalling = false;
    }
  }

  private async doInstallSkill(skill: Skill, settingsToSave: Record<string, string>): Promise<void> {
    if (IS_DEV_MODE) {
      const fakeId = `dev-${Date.now()}`;
      this.installed = [
        ...this.installed,
        {
          userSkill: { id: fakeId, skill_id: skill.id, enabled: true, installed_at: new Date().toISOString() },
          skill,
        },
      ];
      return;
    }

    const userId = this.currentUserId;
    if (!userId) return;

    const resp = await directRestRequest("/user_skills", {
      method: "POST",
      body: JSON.stringify({ user_id: userId, skill_id: skill.id, enabled: true }),
      headers: { Prefer: "return=representation" },
    });

    if (resp.ok) {
      const [created] = await resp.json();
      this.installed = [...this.installed, { userSkill: created, skill }];

      if (Object.keys(settingsToSave).length > 0) {
        this.settingsValues = settingsToSave;
        await this.saveSettings(skill.id);
      }
    } else {
      const errText = await resp.text().catch(() => "");
      this.actionError = errText.includes("duplicate")
        ? "Skill is already installed"
        : "Failed to install skill";
    }
  }

  private async uninstallSkill(userSkillId: string): Promise<void> {
    this.actionError = null;

    if (IS_DEV_MODE) {
      this.installed = this.installed.filter((iv) => iv.userSkill.id !== userSkillId);
      this.expandedSkillId = null;
      return;
    }

    const resp = await directRestRequest(`/user_skills?id=eq.${userSkillId}`, { method: "DELETE" });

    if (resp.ok) {
      this.installed = this.installed.filter((iv) => iv.userSkill.id !== userSkillId);
      if (this.expandedSkillId) this.expandedSkillId = null;
    } else {
      this.actionError = "Failed to uninstall skill";
    }
  }

  private async toggleSkill(userSkillId: string, enabled: boolean): Promise<void> {
    if (IS_DEV_MODE) {
      this.installed = this.installed.map((iv) =>
        iv.userSkill.id === userSkillId ? { ...iv, userSkill: { ...iv.userSkill, enabled } } : iv
      );
      return;
    }

    const resp = await directRestRequest(`/user_skills?id=eq.${userSkillId}`, {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    });

    if (resp.ok) {
      this.installed = this.installed.map((iv) =>
        iv.userSkill.id === userSkillId ? { ...iv, userSkill: { ...iv.userSkill, enabled } } : iv
      );
    }
  }

  private async expandSkillSettings(skillId: string): Promise<void> {
    if (this.expandedSkillId === skillId) { this.expandedSkillId = null; return; }

    this.expandedSkillId = skillId;
    this.settingsValues = {};
    this.settingsHints = {};

    if (IS_DEV_MODE) return;

    try {
      const resp = await callSkillsManage({ action: "get_settings", skillId });
      if (resp.ok) {
        const data = await resp.json();
        this.settingsHints = data.hints || {};
      }
    } catch { /* non-critical */ }
  }

  private async saveSettings(skillId: string): Promise<void> {
    if (Object.keys(this.settingsValues).length === 0) return;

    this.settingsSaving = true;
    try {
      if (IS_DEV_MODE) {
        const newHints: Record<string, { hasValue: boolean; hint?: string }> = { ...this.settingsHints };
        for (const [key, value] of Object.entries(this.settingsValues)) {
          if (value) newHints[key] = { hasValue: true, hint: `••••${value.slice(-4)}` };
        }
        this.settingsHints = newHints;
        this.settingsValues = {};
        return;
      }

      const resp = await callSkillsManage({ action: "save_settings", skillId, settings: this.settingsValues });
      if (resp.ok) {
        await this.expandSkillSettings(skillId);
        this.expandedSkillId = skillId;
        this.settingsValues = {};
      } else {
        this.actionError = "Failed to save settings";
      }
    } finally {
      this.settingsSaving = false;
    }
  }

  // ══════════════════════════════════════════
  // Create / Edit Skill
  // ══════════════════════════════════════════

  private openCreateModal(): void {
    this.showCreateModal = true;
    this.editingSkillId = null;
    this.createTab = "general";
    this.createForm = { name: "", slug: "", description: "", category: "other", prompt: "", tags: "", emoji: "", required_tools: [] };
    this.createCommands = [];
    this.createHttpActions = [];
    this.createSettingsSchema = [];
    this.createError = null;
    this.createSaving = false;
    this.expandedActionIdx = null;
    this.expandedActionSections = {};
    this.expandedSchemaAdvanced = {};
    this.editingSlug = false;
    this.showIconPicker = false;
  }

  private openEditModal(skill: Skill): void {
    this.showCreateModal = true;
    this.editingSkillId = skill.id;
    this.createTab = "general";
    this.createForm = {
      name: skill.name,
      slug: skill.slug,
      description: skill.description,
      category: skill.category,
      prompt: (skill as Record<string, unknown>).prompt as string || "",
      tags: skill.tags.join(", "),
      emoji: skill.emoji || "",
      required_tools: [...(skill.required_tools || [])],
    };
    this.createCommands = [...(skill.commands || [])];
    this.createHttpActions = ((skill.http_actions || []) as SkillHttpAction[]).map((a) => ({ ...a }));
    this.createSettingsSchema = [...(skill.settings_schema || [])].map((s) => ({ ...s }));
    this.createError = null;
    this.createSaving = false;
    this.expandedActionIdx = null;
    this.expandedActionSections = {};
    this.expandedSchemaAdvanced = {};
    this.editingSlug = false;
    this.showIconPicker = false;
  }

  private closeCreateModal(): void {
    this.showCreateModal = false;
    this.editingSkillId = null;
    this.createError = null;
  }

  private addCommand(): void {
    this.createCommands = [...this.createCommands, { name: "", description: "", prepend_prompt: "" }];
  }

  private removeCommand(idx: number): void {
    this.createCommands = this.createCommands.filter((_, i) => i !== idx);
  }

  private async saveSkill(): Promise<void> {
    const { name, slug, description, category, prompt, tags } = this.createForm;

    if (!name.trim()) { this.createError = "Name is required"; return; }
    if (!slug.trim()) { this.createError = "Slug is required"; return; }
    if (!description.trim()) { this.createError = "Description is required"; return; }
    if (!prompt.trim()) { this.createError = "Prompt is required"; return; }

    this.createSaving = true;
    this.createError = null;

    const payload: Record<string, unknown> = {
      name: name.trim(),
      slug: slug.trim().toLowerCase(),
      description: description.trim(),
      category,
      prompt: prompt.trim(),
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      emoji: this.createForm.emoji || null,
      commands: this.createCommands.filter((c) => c.name.trim()),
      required_tools: this.createForm.required_tools,
      http_actions: this.createHttpActions.filter((a) => a.name.trim() && a.url_template.trim()),
      settings_schema: this.createSettingsSchema.filter((s) => s.key.trim() && s.label.trim()),
    };

    try {
      if (IS_DEV_MODE) {
        this.closeCreateModal();
        return;
      }

      if (this.editingSkillId) {
        payload.action = "update_skill";
        payload.skillId = this.editingSkillId;
      } else {
        payload.action = "create_skill";
      }

      const resp = await callSkillsManage(payload);
      if (resp.ok) {
        this.closeCreateModal();
        await this.loadCatalog();
      } else {
        const data = await resp.json().catch(() => ({ error: "Failed to save skill" }));
        this.createError = data.error || "Failed to save skill";
      }
    } catch {
      this.createError = "Network error";
    } finally {
      this.createSaving = false;
    }
  }

  private async deleteOwnSkill(skillId: string): Promise<void> {
    if (!confirm("Delete this skill? This cannot be undone.")) return;

    if (IS_DEV_MODE) {
      this.catalog = this.catalog.filter((s) => s.id !== skillId);
      return;
    }

    const resp = await callSkillsManage({ action: "delete_skill", skillId });
    if (resp.ok) {
      this.catalog = this.catalog.filter((s) => s.id !== skillId);
      if (this.detailSkill?.id === skillId) this.detailSkill = null;
    } else {
      this.actionError = "Failed to delete skill";
    }
  }

  // ══════════════════════════════════════════
  // Detail View & Reviews
  // ══════════════════════════════════════════

  private async openDetail(skill: Skill): Promise<void> {
    this.detailSkill = skill;
    this.detailReviews = [];
    this.detailReviewsTotal = 0;
    this.detailReviewsPage = 1;
    this.reviewRating = 0;
    this.reviewTitle = "";
    this.reviewBody = "";
    await this.loadReviews();
  }

  private closeDetail(): void {
    this.detailSkill = null;
    this.detailReviews = [];
  }

  private async loadReviews(): Promise<void> {
    if (!this.detailSkill || IS_DEV_MODE) return;
    this.detailReviewsLoading = true;

    try {
      const resp = await callSkillsManage({
        action: "get_reviews",
        skillId: this.detailSkill.id,
        page: this.detailReviewsPage,
        pageSize: 10,
      });
      if (resp.ok) {
        const data = await resp.json();
        this.detailReviews = data.reviews || [];
        this.detailReviewsTotal = data.total || 0;
      }
    } catch { /* non-critical */ }
    finally { this.detailReviewsLoading = false; }
  }

  private async submitReview(): Promise<void> {
    if (!this.detailSkill || this.reviewRating < 1) return;

    this.reviewSaving = true;
    try {
      const resp = await callSkillsManage({
        action: "submit_review",
        skillId: this.detailSkill.id,
        rating: this.reviewRating,
        title: this.reviewTitle.trim() || null,
        body: this.reviewBody.trim() || null,
      });
      if (resp.ok) {
        this.reviewRating = 0;
        this.reviewTitle = "";
        this.reviewBody = "";
        await this.loadReviews();
        await this.loadCatalog(); // refresh aggregate stats
      } else {
        const data = await resp.json().catch(() => ({ error: "Failed to submit review" }));
        this.actionError = data.error;
      }
    } finally { this.reviewSaving = false; }
  }

  private async deleteReview(skillId: string): Promise<void> {
    const resp = await callSkillsManage({ action: "delete_review", skillId });
    if (resp.ok) {
      await this.loadReviews();
      await this.loadCatalog();
    }
  }

  // ══════════════════════════════════════════
  // Voting
  // ══════════════════════════════════════════

  private async vote(skillId: string, direction: number): Promise<void> {
    const currentVote = this.userVotes.get(skillId) ?? 0;
    const newVote = currentVote === direction ? 0 : direction;

    // Optimistic update
    const newVotes = new Map(this.userVotes);
    if (newVote === 0) {
      newVotes.delete(skillId);
    } else {
      newVotes.set(skillId, newVote);
    }
    this.userVotes = newVotes;

    // Optimistic score update on catalog
    const scoreDelta = newVote - currentVote;
    this.catalog = this.catalog.map((s) =>
      s.id === skillId ? { ...s, vote_score: (s.vote_score ?? 0) + scoreDelta } : s
    );

    if (IS_DEV_MODE) return;

    const resp = await callSkillsManage({ action: "vote", skillId, vote: newVote });
    if (!resp.ok) {
      // Revert
      const revertVotes = new Map(this.userVotes);
      if (currentVote === 0) revertVotes.delete(skillId); else revertVotes.set(skillId, currentVote);
      this.userVotes = revertVotes;
      this.catalog = this.catalog.map((s) =>
        s.id === skillId ? { ...s, vote_score: (s.vote_score ?? 0) - scoreDelta } : s
      );
    }
  }

  // ══════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════

  protected override render(): TemplateResult {
    return html`
      <div class="main-content-padded view-enter">
        <div class="view-header" style="display:flex;align-items:center;justify-content:space-between;">
          <h1 class="view-title">Skills</h1>
          <button class="btn btn--primary btn--sm" @click=${() => this.openCreateModal()}>
            + Create Skill
          </button>
        </div>

        ${this.actionError
          ? html`<div class="callout callout--error">${this.actionError}</div>`
          : nothing}

        ${this.loading
          ? html`<div class="skills-loading"><span class="spinner"></span> Loading skills...</div>`
          : html`
              ${this.renderInstalled()}
              ${this.renderCatalog()}
            `}
      </div>

      ${this.setupWizardSkill ? this.renderSetupWizard(this.setupWizardSkill) : nothing}
      ${this.showCreateModal ? this.renderCreateModal() : nothing}
      ${this.detailSkill ? this.renderDetailModal(this.detailSkill) : nothing}
    `;
  }

  // ── Setup Wizard (existing) ──

  private renderSetupWizard(skill: Skill): TemplateResult {
    const requiredCount = skill.settings_schema.filter((s) => s.required).length;
    const filledRequired = skill.settings_schema
      .filter((s) => s.required)
      .filter((s) => this.setupWizardValues[s.key]?.trim()).length;
    const canInstall = filledRequired >= requiredCount;

    return html`
      <div class="skill-wizard-backdrop" @click=${() => this.closeSetupWizard()}>
        <div class="skill-wizard" @click=${(e: Event) => e.stopPropagation()}>
          <div class="skill-wizard__header">
            ${renderSkillIcon(skill)}
            <div>
              <div class="skill-wizard__title">Set up ${skill.name}</div>
              <div class="skill-wizard__subtitle">${skill.description}</div>
            </div>
          </div>
          <div class="skill-wizard__body">
            <p class="skill-wizard__intro">This skill requires configuration before it can be used.</p>
            ${skill.settings_schema.map((entry) => html`
              <div class="skill-setting-field">
                <label class="skill-setting-label">
                  ${entry.label}
                  ${entry.required ? html`<span class="skill-setting-required">*</span>` : nothing}
                  ${entry.help_url ? html`<a class="skill-setting-help" href=${entry.help_url} target="_blank" rel="noopener noreferrer">Get key</a>` : nothing}
                </label>
                <div class="skill-setting-input-row">
                  <input type=${entry.type === "secret" ? "password" : "text"} class="skill-setting-input"
                    placeholder=${entry.placeholder ?? ""}
                    .value=${this.setupWizardValues[entry.key] ?? ""}
                    @input=${(e: Event) => { this.setupWizardValues = { ...this.setupWizardValues, [entry.key]: (e.target as HTMLInputElement).value }; }}
                  />
                </div>
              </div>
            `)}
            ${this.setupWizardError ? html`<div class="skill-wizard__error">${this.setupWizardError}</div>` : nothing}
          </div>
          <div class="skill-wizard__footer">
            <button class="btn btn--ghost" @click=${() => this.closeSetupWizard()} ?disabled=${this.setupWizardInstalling}>Cancel</button>
            <button class="btn btn--primary" @click=${() => this.installFromWizard()} ?disabled=${this.setupWizardInstalling || !canInstall}>
              ${this.setupWizardInstalling ? "Installing..." : "Install"}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // ── Installed Section ──

  private renderInstalled(): TemplateResult {
    const views = this.getInstalledViews();
    if (views.length === 0) return html``;

    return html`
      <div class="skills-section">
        <h2 class="skills-section-title">Installed</h2>
        <div class="skills-list">
          ${views.map((iv) => this.renderInstalledCard(iv))}
        </div>
      </div>
    `;
  }

  private renderInstalledCard(iv: InstalledSkillView): TemplateResult {
    const { skill, userSkill } = iv;
    const isExpanded = this.expandedSkillId === skill.id;
    const hasSettings = skill.settings_schema && skill.settings_schema.length > 0;
    const hasCommands = skill.commands && skill.commands.length > 0;

    return html`
      <div class="skill-card skill-card--installed ${isExpanded ? "skill-card--expanded" : ""}">
        <div class="skill-card__main">
          ${renderSkillIcon(skill)}
          <div class="skill-card__info">
            <div class="skill-card__name">${skill.name}</div>
            <div class="skill-card__desc">${skill.description}</div>
            ${hasCommands
              ? html`<div class="skill-card__commands">
                  ${skill.commands.map((cmd) => html`<span class="skill-card__command">/${cmd.name}</span>`)}
                </div>`
              : nothing}
          </div>
          <div class="skill-card__actions">
            <label class="skill-toggle">
              <input type="checkbox" .checked=${userSkill.enabled}
                @change=${(e: Event) => { this.toggleSkill(userSkill.id, (e.target as HTMLInputElement).checked); }}
              />
              <span class="skill-toggle__label">${userSkill.enabled ? "On" : "Off"}</span>
            </label>
            ${hasSettings
              ? html`<button class="btn btn--ghost btn--sm" @click=${() => this.expandSkillSettings(skill.id)} title="Settings">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>`
              : nothing}
            <button class="btn btn--ghost btn--sm btn--danger" @click=${() => this.uninstallSkill(userSkill.id)} title="Uninstall">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        </div>

        ${isExpanded && hasSettings
          ? html`
              <div class="skill-settings">
                <div class="skill-settings__title">Settings</div>
                ${skill.settings_schema.map((entry) => this.renderSettingField(entry))}
                <div class="skill-settings__actions">
                  <button class="btn btn--primary btn--sm"
                    ?disabled=${this.settingsSaving || Object.keys(this.settingsValues).length === 0}
                    @click=${() => this.saveSettings(skill.id)}>
                    ${this.settingsSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private renderSettingField(entry: SettingsSchemaEntry): TemplateResult {
    const hint = this.settingsHints[entry.key];
    const currentValue = this.settingsValues[entry.key] ?? "";
    const isSecret = entry.type === "secret";

    return html`
      <div class="skill-setting-field">
        <label class="skill-setting-label">
          ${entry.label}
          ${entry.required ? html`<span class="skill-setting-required">*</span>` : nothing}
          ${entry.help_url ? html`<a class="skill-setting-help" href=${entry.help_url} target="_blank" rel="noopener noreferrer">Get key</a>` : nothing}
        </label>
        <div class="skill-setting-input-row">
          <input type=${isSecret ? "password" : "text"} class="skill-setting-input"
            placeholder=${hint?.hasValue ? `Currently set (${hint.hint ?? "****"})` : entry.placeholder ?? ""}
            .value=${currentValue}
            @input=${(e: Event) => { this.settingsValues = { ...this.settingsValues, [entry.key]: (e.target as HTMLInputElement).value }; }}
          />
        </div>
      </div>
    `;
  }

  // ── Catalog Section ──

  private renderCatalog(): TemplateResult {
    const available = this.getAvailableSkills();

    return html`
      <div class="skills-section">
        <h2 class="skills-section-title">Browse Skills</h2>

        <!-- Filter tabs -->
        <div class="skills-filter-tabs">
          ${FILTER_TABS.map((tab) => html`
            <button class="skills-filter-tab ${this.filterTab === tab.id ? "skills-filter-tab--active" : ""}"
              @click=${() => { this.filterTab = tab.id; }}>
              ${tab.label}
            </button>
          `)}
        </div>

        <div class="skills-filters">
          <div class="skills-categories">
            ${CATEGORIES.map((cat) => html`
              <button class="skills-category-btn ${this.category === cat.id ? "skills-category-btn--active" : ""}"
                @click=${() => { this.category = cat.id; }}>
                ${cat.label}
              </button>
            `)}
          </div>
          <div class="skills-filters-row">
            <input type="text" class="skills-search" placeholder="Search skills..."
              .value=${this.search}
              @input=${(e: Event) => { this.search = (e.target as HTMLInputElement).value; }}
            />
            <select class="skills-sort" .value=${this.sortBy}
              @change=${(e: Event) => { this.sortBy = (e.target as HTMLSelectElement).value as SortOption; }}>
              ${SORT_OPTIONS.map((opt) => html`<option value=${opt.id}>${opt.label}</option>`)}
            </select>
          </div>
        </div>

        ${available.length === 0
          ? html`<div class="skills-empty">
              ${this.filterTab === "my_skills" ? "You haven't created any skills yet."
                : this.catalog.length === 0 ? "No skills available yet."
                : "No skills match your filters."}
            </div>`
          : html`<div class="skills-grid">
              ${available.map((skill) => this.renderCatalogCard(skill))}
            </div>`}
      </div>
    `;
  }

  private renderCatalogCard(skill: Skill): TemplateResult {
    const hasCommands = skill.commands && skill.commands.length > 0;
    const isOwn = skill.user_id === this.currentUserId;
    const installedIds = new Set(this.installed.map((iv) => iv.userSkill.skill_id));
    const isInstalled = installedIds.has(skill.id);
    const userVote = this.userVotes.get(skill.id) ?? 0;
    const installs = skill.install_count ?? 0;
    const rating = skill.average_rating ?? 0;

    return html`
      <div class="skill-card skill-card--catalog" @click=${() => this.openDetail(skill)}>
        <!-- Header: icon + name + badge -->
        <div class="skill-card__header">
          ${renderSkillIcon(skill)}
          <div class="skill-card__title">
            ${skill.name}
            ${skill.is_bundled ? html`<span class="skill-card__badge skill-card__badge--official">Official</span>` : nothing}
          </div>
        </div>

        <!-- Description -->
        <div class="skill-card__desc">${skill.description}</div>

        <!-- Tags row: category + command -->
        <div class="skill-card__tags">
          <span class="skill-card__category">${skill.category}</span>
          ${hasCommands ? html`<span class="skill-card__badge">/${skill.commands[0].name}</span>` : nothing}
        </div>

        <!-- Footer: stats left, actions right -->
        <div class="skill-card__footer" @click=${(e: Event) => e.stopPropagation()}>
          <div class="skill-card__stats">
            ${installs > 0 ? html`<span class="skill-card__stat"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>${installs}</span>` : nothing}
            ${rating > 0 ? html`<span class="skill-card__stat">${renderStars(rating)} <span class="skill-card__stat-num">${rating}</span></span>` : nothing}
            <div class="skill-vote">
              <button class="skill-vote__btn ${userVote === 1 ? "skill-vote__btn--active" : ""}"
                @click=${() => this.vote(skill.id, 1)} title="Upvote">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="${userVote === 1 ? "currentColor" : "none"}" stroke="currentColor" stroke-width="2"><path d="M12 5l-8 9h5v6h6v-6h5z"/></svg>
              </button>
              <span class="skill-vote__score">${skill.vote_score ?? 0}</span>
              <button class="skill-vote__btn ${userVote === -1 ? "skill-vote__btn--active-down" : ""}"
                @click=${() => this.vote(skill.id, -1)} title="Downvote">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="${userVote === -1 ? "currentColor" : "none"}" stroke="currentColor" stroke-width="2"><path d="M12 19l8-9h-5V4H9v6H4z"/></svg>
              </button>
            </div>
          </div>
          <div class="skill-card__actions">
            ${isOwn
              ? html`
                  <button class="btn btn--ghost btn--sm" @click=${() => this.openEditModal(skill)}>Edit</button>
                  <button class="btn btn--ghost btn--sm btn--danger" @click=${() => this.deleteOwnSkill(skill.id)}>Delete</button>
                `
              : isInstalled
                ? html`<span class="skill-card__installed-badge">Installed</span>`
                : html`<button class="btn btn--primary btn--sm" @click=${() => this.installSkill(skill.id)}>Install</button>`}
          </div>
        </div>
      </div>
    `;
  }

  // ── Create / Edit Modal ──

  private getTabSubtitle(): string {
    switch (this.createTab) {
      case "general": return "Set up the basics — name, description, and category";
      case "prompt": return "Write the instructions your AI follows";
      case "actions": return "Connect to external APIs and services";
      case "settings": return "Define what users need to configure";
      case "advanced": return "Slash commands and capability requirements";
    }
  }

  private toggleActionSection(actionIdx: number, section: string): void {
    const key = `${actionIdx}-${section}`;
    this.expandedActionSections = { ...this.expandedActionSections, [key]: !this.expandedActionSections[key] };
  }

  private isActionSectionOpen(actionIdx: number, section: string): boolean {
    return !!this.expandedActionSections[`${actionIdx}-${section}`];
  }

  // ── Markdown toolbar helpers ──

  private insertMarkdownSyntax(prefix: string, suffix: string): void {
    const ta = this.promptTextarea;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    const selected = text.slice(start, end);
    const replacement = prefix + (selected || "text") + suffix;
    const newValue = text.slice(0, start) + replacement + text.slice(end);
    this.createForm = { ...this.createForm, prompt: newValue };
    requestAnimationFrame(() => {
      ta.focus();
      const cursorPos = start + prefix.length + (selected ? selected.length : 4);
      ta.setSelectionRange(cursorPos, cursorPos);
    });
  }

  // ── HTTP Action helpers ──

  private addHttpAction(): void {
    this.createHttpActions = [...this.createHttpActions, {
      id: "", name: "", description: "", method: "GET",
      url_template: "", headers: {}, params: {}, settings_keys: [],
      input_params: [], response_path: "", timeout_ms: 10000,
    }];
  }

  private removeHttpAction(idx: number): void {
    this.createHttpActions = this.createHttpActions.filter((_, i) => i !== idx);
  }

  private updateHttpAction(idx: number, partial: Partial<SkillHttpAction>): void {
    const actions = [...this.createHttpActions];
    actions[idx] = { ...actions[idx], ...partial };
    // Auto-generate id from name
    if (partial.name !== undefined) {
      actions[idx].id = slugify(partial.name);
    }
    this.createHttpActions = actions;
  }

  private addActionInputParam(actionIdx: number): void {
    const actions = [...this.createHttpActions];
    const params = [...(actions[actionIdx].input_params || [])];
    params.push({ name: "", type: "string", description: "", required: false });
    actions[actionIdx] = { ...actions[actionIdx], input_params: params };
    this.createHttpActions = actions;
  }

  private removeActionInputParam(actionIdx: number, paramIdx: number): void {
    const actions = [...this.createHttpActions];
    const params = [...(actions[actionIdx].input_params || [])].filter((_, i) => i !== paramIdx);
    actions[actionIdx] = { ...actions[actionIdx], input_params: params };
    this.createHttpActions = actions;
  }

  private updateActionInputParam(actionIdx: number, paramIdx: number, partial: Partial<{ name: string; type: string; description: string; required: boolean }>): void {
    const actions = [...this.createHttpActions];
    const params = [...(actions[actionIdx].input_params || [])];
    params[paramIdx] = { ...params[paramIdx], ...partial };
    actions[actionIdx] = { ...actions[actionIdx], input_params: params };
    this.createHttpActions = actions;
  }

  private addActionKv(actionIdx: number, field: "headers" | "params"): void {
    const actions = [...this.createHttpActions];
    const obj = { ...(actions[actionIdx][field] || {}) };
    obj[""] = "";
    actions[actionIdx] = { ...actions[actionIdx], [field]: obj };
    this.createHttpActions = actions;
  }

  private updateActionKv(actionIdx: number, field: "headers" | "params", oldKey: string, newKey: string, newValue: string): void {
    const actions = [...this.createHttpActions];
    const obj = { ...(actions[actionIdx][field] || {}) };
    if (oldKey !== newKey) delete obj[oldKey];
    obj[newKey] = newValue;
    actions[actionIdx] = { ...actions[actionIdx], [field]: obj };
    this.createHttpActions = actions;
  }

  private removeActionKv(actionIdx: number, field: "headers" | "params", key: string): void {
    const actions = [...this.createHttpActions];
    const obj = { ...(actions[actionIdx][field] || {}) };
    delete obj[key];
    actions[actionIdx] = { ...actions[actionIdx], [field]: obj };
    this.createHttpActions = actions;
  }

  // ── Settings schema helpers ──

  private addSettingEntry(): void {
    this.createSettingsSchema = [...this.createSettingsSchema, {
      key: "", label: "", type: "string", required: false, placeholder: "", help_url: "",
    }];
  }

  private removeSettingEntry(idx: number): void {
    this.createSettingsSchema = this.createSettingsSchema.filter((_, i) => i !== idx);
  }

  private updateSettingEntry(idx: number, partial: Partial<SettingsSchemaEntry>): void {
    const schema = [...this.createSettingsSchema];
    schema[idx] = { ...schema[idx], ...partial };
    // Auto-generate key from label
    if (partial.label !== undefined) {
      schema[idx].key = partial.label.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "");
    }
    this.createSettingsSchema = schema;
  }

  // ── Tab rendering ──

  private renderCreateModal(): TemplateResult {
    const isEditing = !!this.editingSkillId;
    const canSave = this.createForm.name.trim() && this.createForm.slug.trim() && this.createForm.prompt.trim();

    const tabs: { id: CreateTab; label: string }[] = [
      { id: "general", label: "Basics" },
      { id: "prompt", label: "Instructions" },
      { id: "actions", label: "API Actions" },
      { id: "settings", label: "User Settings" },
      { id: "advanced", label: "More Options" },
    ];

    return html`
      <div class="skill-wizard-backdrop" @click=${() => this.closeCreateModal()}>
        <div class="skill-wizard skill-wizard--wide" @click=${(e: Event) => e.stopPropagation()}>
          <div class="skill-wizard__header">
            <div>
              <div class="skill-wizard__title">${isEditing ? "Edit Skill" : "Create a Skill"}</div>
              <div class="skill-wizard__subtitle">${this.getTabSubtitle()}</div>
            </div>
          </div>

          <div class="skill-wizard__tabs">
            ${tabs.map((tab) => html`
              <button class="skill-wizard__tab ${this.createTab === tab.id ? "skill-wizard__tab--active" : ""}"
                @click=${() => { this.createTab = tab.id; }}>
                ${tab.label}
              </button>
            `)}
          </div>

          <div class="skill-wizard__body">
            ${this.createTab === "general" ? this.renderTabGeneral() : nothing}
            ${this.createTab === "prompt" ? this.renderTabPrompt() : nothing}
            ${this.createTab === "actions" ? this.renderTabActions() : nothing}
            ${this.createTab === "settings" ? this.renderTabSettings() : nothing}
            ${this.createTab === "advanced" ? this.renderTabAdvanced() : nothing}

            ${this.createError ? html`<div class="skill-wizard__error">${this.createError}</div>` : nothing}
          </div>

          <div class="skill-wizard__footer">
            <button class="btn btn--ghost" @click=${() => this.closeCreateModal()} ?disabled=${this.createSaving}>Cancel</button>
            <button class="btn btn--primary" @click=${() => this.saveSkill()} ?disabled=${this.createSaving || !canSave}>
              ${this.createSaving ? "Saving..." : isEditing ? "Save Changes" : "Create Skill"}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // ── Tab: General ──

  private renderTabGeneral(): TemplateResult {
    return html`
      <!-- Name + Emoji row -->
      <div class="skill-name-row">
        <div class="skill-setting-field">
          <label class="skill-setting-label">Name <span class="skill-setting-required">*</span></label>
          <input type="text" class="skill-setting-input" placeholder="My Awesome Skill" maxlength="100"
            .value=${this.createForm.name}
            @input=${(e: Event) => {
              const name = (e.target as HTMLInputElement).value;
              this.createForm = { ...this.createForm, name, slug: this.editingSkillId ? this.createForm.slug : slugify(name) };
            }}
          />
          ${this.createForm.slug ? html`
            <div class="skill-slug-display">
              <span class="skill-slug-display__value">${this.createForm.slug}</span>
              ${this.editingSlug ? html`
                <input type="text" class="skill-setting-input skill-slug-input"
                  maxlength="50"
                  .value=${this.createForm.slug}
                  @input=${(e: Event) => { this.createForm = { ...this.createForm, slug: (e.target as HTMLInputElement).value }; }}
                  @blur=${() => { this.editingSlug = false; }}
                />
              ` : html`
                <button class="skill-slug-display__edit" @click=${() => { this.editingSlug = true; }}>edit</button>
              `}
            </div>
          ` : nothing}
        </div>
        <div class="skill-icon-picker">
          <label class="skill-setting-label">Icon</label>
          <button type="button" class="skill-icon-picker__preview"
            @click=${() => { this.showIconPicker = !this.showIconPicker; }}>
            ${unsafeHTML(this.createForm.emoji && SKILL_ICONS[this.createForm.emoji] ? SKILL_ICONS[this.createForm.emoji] : DEFAULT_SKILL_ICON)}
          </button>
          ${this.showIconPicker ? html`
            <div class="skill-icon-picker__dropdown">
              ${[["", DEFAULT_SKILL_ICON], ...Object.entries(SKILL_ICONS)].map(([key, svg]) => html`
                <button type="button" class="skill-icon-picker__option ${this.createForm.emoji === key ? "skill-icon-picker__option--selected" : ""}"
                  @click=${() => { this.createForm = { ...this.createForm, emoji: key }; this.showIconPicker = false; }}>
                  ${unsafeHTML(svg as string)}
                </button>
              `)}
            </div>
          ` : nothing}
        </div>
      </div>

      <div class="skill-setting-field">
        <label class="skill-setting-label">Description <span class="skill-setting-required">*</span></label>
        <input type="text" class="skill-setting-input"
          placeholder="Briefly describe what this skill does"
          maxlength="300"
          .value=${this.createForm.description}
          @input=${(e: Event) => { this.createForm = { ...this.createForm, description: (e.target as HTMLInputElement).value }; }}
        />
        <div class="skill-field-helper">This helps users and the AI understand when to use your skill</div>
      </div>

      <div class="skill-cat-tags-row">
        <div class="skill-setting-field skill-setting-field--flush">
          <label class="skill-setting-label">Category</label>
          <select class="skill-setting-input" .value=${this.createForm.category}
            @change=${(e: Event) => { this.createForm = { ...this.createForm, category: (e.target as HTMLSelectElement).value }; }}>
            ${CATEGORIES.filter((c) => c.id !== "all").map((c) => html`<option value=${c.id}>${c.label}</option>`)}
          </select>
        </div>

        <div class="skill-setting-field skill-setting-field--flush">
          <label class="skill-setting-label">Tags</label>
          <input type="text" class="skill-setting-input"
            placeholder="weather, forecast, news"
            .value=${this.createForm.tags}
            @input=${(e: Event) => { this.createForm = { ...this.createForm, tags: (e.target as HTMLInputElement).value }; }}
          />
          <div class="skill-field-helper">Comma-separated keywords that help users find this skill</div>
        </div>
      </div>
    `;
  }

  // ── Tab: Instructions (Prompt with Markdown Editor) ──

  private renderTabPrompt(): TemplateResult {
    const len = this.createForm.prompt.length;
    const counterClass = len > 7500 ? "skill-char-counter--danger" : len > 6000 ? "skill-char-counter--warn" : "";
    return html`
      <div class="skill-setting-field">
        <label class="skill-setting-label">Instructions <span class="skill-setting-required">*</span></label>

        <div class="skill-prompt-editor">
          <div class="skill-prompt-editor__toolbar">
            <button type="button" title="Bold" @click=${() => this.insertMarkdownSyntax("**", "**")}>Bold</button>
            <button type="button" title="Italic" @click=${() => this.insertMarkdownSyntax("_", "_")}>Italic</button>
            <span class="skill-prompt-toolbar__sep"></span>
            <button type="button" title="Heading" @click=${() => this.insertMarkdownSyntax("## ", "")}>H2</button>
            <button type="button" title="Inline code" @click=${() => this.insertMarkdownSyntax("`", "`")}>Code</button>
            <button type="button" title="Code block" @click=${() => this.insertMarkdownSyntax("```\n", "\n```")}>Block</button>
            <button type="button" title="Bullet list" @click=${() => this.insertMarkdownSyntax("- ", "")}>List</button>
            <button type="button" title="Hyperlink" @click=${() => this.insertMarkdownSyntax("[", "](url)")}>Link</button>
          </div>
          <div class="skill-prompt-editor__pane-labels">
            <span>Edit</span>
            <span>Preview</span>
          </div>
          <div class="skill-prompt-editor__panes">
            <div class="skill-prompt-editor__edit">
              <textarea class="skill-prompt-textarea" rows="12"
                placeholder="Write your skill instructions here using markdown...&#10;&#10;Example:&#10;You are a helpful weather assistant.&#10;When the user asks about weather, use the get_weather action."
                .value=${this.createForm.prompt}
                @input=${(e: Event) => { this.createForm = { ...this.createForm, prompt: (e.target as HTMLTextAreaElement).value }; }}
              ></textarea>
            </div>
            <div class="skill-prompt-editor__preview markdown-body">
              ${this.createForm.prompt.trim()
                ? unsafeHTML(renderSkillMarkdown(this.createForm.prompt))
                : html`<div class="skill-prompt-editor__placeholder">Preview will appear here as you type...</div>`}
            </div>
          </div>
          <div class="skill-char-counter ${counterClass}">${len}/8000</div>
        </div>
      </div>
    `;
  }

  // ── Tab: HTTP Actions ──

  private get trashIcon() {
    return html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>`;
  }

  private renderTabActions(): TemplateResult {
    if (this.createHttpActions.length === 0) {
      return html`
        <div class="skill-empty-state">
          <div class="skill-empty-state__icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </div>
          <div class="skill-empty-state__title">No API actions yet</div>
          <div class="skill-empty-state__desc">
            API actions let your skill connect to external services.
            The AI will call these endpoints when your skill is triggered.
          </div>
          <button class="btn btn--primary btn--sm" @click=${() => { this.addHttpAction(); this.expandedActionIdx = 0; }}>+ Add API Action</button>
        </div>
      `;
    }

    return html`
      <div class="skill-tab-desc">API actions let your skill call external services. The AI automatically handles these calls when your skill is triggered.</div>

      ${this.createHttpActions.map((action, idx) => this.renderActionCard(action, idx))}

      <button class="btn btn--ghost btn--sm" @click=${() => { this.addHttpAction(); this.expandedActionIdx = this.createHttpActions.length; }}>+ Add Action</button>
    `;
  }

  private renderActionCard(action: SkillHttpAction, idx: number): TemplateResult {
    const isExpanded = this.expandedActionIdx === idx;
    const methodClass = `skill-action-method-badge skill-action-method-badge--${action.method.toLowerCase()}`;
    const headers = Object.entries(action.headers || {});
    const params = Object.entries(action.params || {});
    const inputParamCount = (action.input_params || []).length;
    const headersParamCount = headers.length + params.length;
    const chevronSvg = html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>`;
    const rightChevronSvg = html`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 6 15 12 9 18"/></svg>`;

    return html`
      <div class="skill-action-card ${!isExpanded ? "skill-action-card--collapsed" : ""}"
        @click=${!isExpanded ? () => { this.expandedActionIdx = idx; } : null}>
        <div class="skill-action-card__summary ${isExpanded ? "skill-action-card__summary--clickable" : ""}"
          @click=${isExpanded ? () => { this.expandedActionIdx = null; } : null}>
          <span class=${methodClass}>${action.method}</span>
          <div class="skill-action-card__summary-info">
            <div class="skill-action-card__summary-name">${action.name || "Untitled action"}</div>
            ${!isExpanded && action.description ? html`<div class="skill-action-card__summary-desc">${action.description}</div>` : nothing}
          </div>
          <span class="skill-action-card__chevron ${isExpanded ? "skill-action-card__chevron--open" : ""}">${chevronSvg}</span>
        </div>

        <div class="skill-collapsible ${isExpanded ? "skill-collapsible--open" : ""}">
          <div class="skill-collapsible__inner">
            <div class="skill-action-card__body">
              <label class="skill-setting-label skill-setting-label--sm">Action</label>
              <div class="skill-action-card__header">
                <select class="skill-setting-input skill-select--method"
                  .value=${action.method}
                  @change=${(e: Event) => this.updateHttpAction(idx, { method: (e.target as HTMLSelectElement).value as SkillHttpAction["method"] })}>
                  ${HTTP_METHODS.map((m) => html`<option value=${m}>${m}</option>`)}
                </select>
                <input type="text" class="skill-setting-input skill-flex-1" placeholder="Action name"
                  .value=${action.name}
                  @input=${(e: Event) => this.updateHttpAction(idx, { name: (e.target as HTMLInputElement).value })}
                />
                <button class="skill-delete-btn" title="Remove action" @click=${(e: Event) => { e.stopPropagation(); this.removeHttpAction(idx); this.expandedActionIdx = null; }}>${this.trashIcon}</button>
              </div>

              <div class="skill-setting-field skill-setting-field--tight">
                <label class="skill-setting-label skill-setting-label--sm">Description</label>
                <input type="text" class="skill-setting-input" placeholder="What does this action do?"
                  .value=${action.description}
                  @input=${(e: Event) => this.updateHttpAction(idx, { description: (e.target as HTMLInputElement).value })}
                />
              </div>

              <div class="skill-setting-field skill-setting-field--flush">
                <label class="skill-setting-label skill-setting-label--sm">URL</label>
                <input type="text" class="skill-setting-input" placeholder="https://api.example.com/v1/endpoint"
                  .value=${action.url_template}
                  @input=${(e: Event) => this.updateHttpAction(idx, { url_template: (e.target as HTMLInputElement).value })}
                />
                <div class="skill-field-helper">Use {{KEY}} for dynamic values from settings or AI input</div>
              </div>

              <!-- Parameters sub-section -->
              <div class="skill-action-subsection">
                <button class="skill-action-subsection__toggle" type="button"
                  @click=${() => this.toggleActionSection(idx, "params")}>
                  <span class="skill-action-subsection__toggle-left">
                    <span class="skill-action-subsection__chevron ${this.isActionSectionOpen(idx, "params") ? "skill-action-subsection__chevron--open" : ""}">${rightChevronSvg}</span>
                    Parameters
                    ${inputParamCount > 0 ? html`<span class="skill-action-subsection__badge">${inputParamCount}</span>` : nothing}
                  </span>
                </button>
                <div class="skill-collapsible ${this.isActionSectionOpen(idx, "params") ? "skill-collapsible--open" : ""}">
                  <div class="skill-collapsible__inner">
                    <div class="skill-action-subsection__content">
                      <div class="skill-field-helper skill-field-helper--bottom">Values the AI provides when calling this action</div>
                      ${(action.input_params || []).map((param, pIdx) => html`
                        <div class="skill-action-kv-row">
                          <input type="text" class="skill-setting-input skill-flex-1" placeholder="name"
                            .value=${param.name}
                            @input=${(e: Event) => this.updateActionInputParam(idx, pIdx, { name: (e.target as HTMLInputElement).value })}
                          />
                          <select class="skill-setting-input skill-select--param-type"
                            .value=${param.type}
                            @change=${(e: Event) => this.updateActionInputParam(idx, pIdx, { type: (e.target as HTMLSelectElement).value })}>
                            <option value="string">text</option>
                            <option value="number">number</option>
                            <option value="boolean">yes/no</option>
                          </select>
                          <input type="text" class="skill-setting-input skill-flex-2" placeholder="description"
                            .value=${param.description}
                            @input=${(e: Event) => this.updateActionInputParam(idx, pIdx, { description: (e.target as HTMLInputElement).value })}
                          />
                          <label class="skill-toggle-switch skill-toggle-switch--compact">
                            <input type="checkbox" .checked=${param.required || false}
                              @change=${(e: Event) => this.updateActionInputParam(idx, pIdx, { required: (e.target as HTMLInputElement).checked })}
                            />
                            <span class="skill-toggle-switch__track"></span>
                            Req
                          </label>
                          <button class="skill-delete-btn" title="Remove parameter" @click=${() => this.removeActionInputParam(idx, pIdx)}>${this.trashIcon}</button>
                        </div>
                      `)}
                      <button class="btn btn--ghost btn--sm" @click=${() => this.addActionInputParam(idx)}>+ Add Parameter</button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Headers & Params sub-section -->
              <div class="skill-action-subsection">
                <button class="skill-action-subsection__toggle" type="button"
                  @click=${() => this.toggleActionSection(idx, "headers")}>
                  <span class="skill-action-subsection__toggle-left">
                    <span class="skill-action-subsection__chevron ${this.isActionSectionOpen(idx, "headers") ? "skill-action-subsection__chevron--open" : ""}">${rightChevronSvg}</span>
                    Headers & ${action.method === "GET" || action.method === "DELETE" ? "Query" : "Body"} Params
                    ${headersParamCount > 0 ? html`<span class="skill-action-subsection__badge">${headersParamCount}</span>` : nothing}
                  </span>
                </button>
                <div class="skill-collapsible ${this.isActionSectionOpen(idx, "headers") ? "skill-collapsible--open" : ""}">
                  <div class="skill-collapsible__inner">
                    <div class="skill-action-subsection__content">
                      <div class="skill-field-helper skill-field-helper--bottom">HTTP headers</div>
                      ${headers.map(([key, val]) => html`
                        <div class="skill-action-kv-row">
                          <input type="text" class="skill-setting-input skill-flex-1" placeholder="Header name"
                            .value=${key}
                            @change=${(e: Event) => this.updateActionKv(idx, "headers", key, (e.target as HTMLInputElement).value, val)}
                          />
                          <input type="text" class="skill-setting-input skill-flex-2" placeholder="Value (e.g. Bearer {{API_KEY}})"
                            .value=${val}
                            @input=${(e: Event) => this.updateActionKv(idx, "headers", key, key, (e.target as HTMLInputElement).value)}
                          />
                          <button class="skill-delete-btn" title="Remove header" @click=${() => this.removeActionKv(idx, "headers", key)}>${this.trashIcon}</button>
                        </div>
                      `)}
                      <button class="btn btn--ghost btn--sm" @click=${() => this.addActionKv(idx, "headers")}>+ Add Header</button>

                      <div class="skill-field-helper skill-field-helper--spaced">${action.method === "GET" || action.method === "DELETE" ? "Query parameters" : "Body parameters"}</div>
                      ${params.map(([key, val]) => html`
                        <div class="skill-action-kv-row">
                          <input type="text" class="skill-setting-input skill-flex-1" placeholder="Key"
                            .value=${key}
                            @change=${(e: Event) => this.updateActionKv(idx, "params", key, (e.target as HTMLInputElement).value, val)}
                          />
                          <input type="text" class="skill-setting-input skill-flex-2" placeholder="Value"
                            .value=${val}
                            @input=${(e: Event) => this.updateActionKv(idx, "params", key, key, (e.target as HTMLInputElement).value)}
                          />
                          <button class="skill-delete-btn" title="Remove param" @click=${() => this.removeActionKv(idx, "params", key)}>${this.trashIcon}</button>
                        </div>
                      `)}
                      <button class="btn btn--ghost btn--sm" @click=${() => this.addActionKv(idx, "params")}>+ Add Param</button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Advanced sub-section -->
              <div class="skill-action-subsection">
                <button class="skill-action-subsection__toggle" type="button"
                  @click=${() => this.toggleActionSection(idx, "advanced")}>
                  <span class="skill-action-subsection__toggle-left">
                    <span class="skill-action-subsection__chevron ${this.isActionSectionOpen(idx, "advanced") ? "skill-action-subsection__chevron--open" : ""}">${rightChevronSvg}</span>
                    Advanced
                  </span>
                </button>
                <div class="skill-collapsible ${this.isActionSectionOpen(idx, "advanced") ? "skill-collapsible--open" : ""}">
                  <div class="skill-collapsible__inner">
                    <div class="skill-action-subsection__content">
                      <div class="skill-action-kv-row">
                        <div class="skill-flex-1">
                          <label class="skill-setting-label skill-setting-label--sm">Response Path</label>
                          <input type="text" class="skill-setting-input" placeholder="$.data.results"
                            .value=${action.response_path || ""}
                            @input=${(e: Event) => this.updateHttpAction(idx, { response_path: (e.target as HTMLInputElement).value })}
                          />
                          <div class="skill-field-helper">JSONPath to extract from the API response</div>
                        </div>
                        <div class="skill-select--timeout">
                          <label class="skill-setting-label skill-setting-label--sm">Timeout</label>
                          <input type="number" class="skill-setting-input" placeholder="10000" min="1000" max="15000"
                            .value=${String(action.timeout_ms || 10000)}
                            @input=${(e: Event) => this.updateHttpAction(idx, { timeout_ms: parseInt((e.target as HTMLInputElement).value) || 10000 })}
                          />
                          <div class="skill-field-helper">In milliseconds</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // ── Tab: Settings Schema ──

  private renderTabSettings(): TemplateResult {
    const rightChevronSvg = html`<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 6 15 12 9 18"/></svg>`;

    if (this.createSettingsSchema.length === 0) {
      return html`
        <div class="skill-empty-state">
          <div class="skill-empty-state__icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </div>
          <div class="skill-empty-state__title">No settings needed</div>
          <div class="skill-empty-state__desc">
            Add settings if your skill needs user-provided information like API keys or preferences.
            Users configure these when they install your skill.
          </div>
          <button class="btn btn--primary btn--sm" @click=${() => this.addSettingEntry()}>+ Add Setting</button>
        </div>
      `;
    }

    return html`
      <div class="skill-tab-desc">Settings are filled in by users when they install your skill. Use these for API keys, preferences, or any user-specific configuration.</div>

      ${this.createSettingsSchema.map((entry, idx) => {
        const isAdvOpen = !!this.expandedSchemaAdvanced[idx];
        return html`
          <div class="skill-schema-row">
            <div class="skill-schema-row__primary">
              <div class="skill-flex-1">
                <label class="skill-setting-label">Label <span class="skill-setting-required">*</span></label>
                <input type="text" class="skill-setting-input" placeholder="e.g. API Key"
                  .value=${entry.label}
                  @input=${(e: Event) => {
                    const label = (e.target as HTMLInputElement).value;
                    const autoKey = label.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "");
                    this.updateSettingEntry(idx, { label, ...(!entry.key || entry.key === this.createSettingsSchema[idx]?.key ? {} : {}), key: autoKey });
                  }}
                />
              </div>

              <div class="skill-schema-row__type">
                <label class="skill-setting-label">Type</label>
                <select class="skill-setting-input"
                  .value=${entry.type}
                  @change=${(e: Event) => this.updateSettingEntry(idx, { type: (e.target as HTMLSelectElement).value as SettingsSchemaEntry["type"] })}>
                  <option value="secret">Secret (hidden)</option>
                  <option value="string">Text</option>
                  <option value="boolean">Toggle (yes/no)</option>
                  <option value="select">Dropdown</option>
                </select>
              </div>

              <div>
                <label class="skill-setting-label">Required</label>
                <div class="skill-schema-row__toggle-cell">
                  <label class="skill-toggle-switch">
                    <input type="checkbox" .checked=${entry.required || false}
                      @change=${(e: Event) => this.updateSettingEntry(idx, { required: (e.target as HTMLInputElement).checked })}
                    />
                    <span class="skill-toggle-switch__track"></span>
                  </label>
                </div>
              </div>

              <button class="skill-delete-btn skill-schema-row__delete" title="Remove setting" @click=${() => this.removeSettingEntry(idx)}>${this.trashIcon}</button>
            </div>

            <div class="skill-collapsible ${entry.type === "select" ? "skill-collapsible--open" : ""}">
              <div class="skill-collapsible__inner">
                <div class="skill-schema-row__options">
                  <label class="skill-setting-label">Options</label>
                  <input type="text" class="skill-setting-input" placeholder="Option 1, Option 2, Option 3"
                    .value=${(entry.options || []).join(", ")}
                    @input=${(e: Event) => {
                      const schema = [...this.createSettingsSchema];
                      schema[idx] = { ...schema[idx], options: (e.target as HTMLInputElement).value.split(",").map((o) => o.trim()).filter(Boolean) };
                      this.createSettingsSchema = schema;
                    }}
                  />
                  <div class="skill-field-helper">Comma-separated list of choices</div>
                </div>
              </div>
            </div>

            <button class="skill-schema-row__advanced-toggle" type="button"
              @click=${() => { this.expandedSchemaAdvanced = { ...this.expandedSchemaAdvanced, [idx]: !isAdvOpen }; }}>
              <span class="skill-schema-row__advanced-chevron ${isAdvOpen ? "skill-schema-row__advanced-chevron--open" : ""}">${rightChevronSvg}</span>
              Advanced options
            </button>

            <div class="skill-collapsible ${isAdvOpen ? "skill-collapsible--open" : ""}">
              <div class="skill-collapsible__inner">
                <div class="skill-schema-row__advanced">
                  <div>
                    <label class="skill-setting-label">Key</label>
                    <input type="text" class="skill-setting-input" placeholder="AUTO_GENERATED"
                      .value=${entry.key}
                      @input=${(e: Event) => {
                        const schema = [...this.createSettingsSchema];
                        schema[idx] = { ...schema[idx], key: (e.target as HTMLInputElement).value };
                        this.createSettingsSchema = schema;
                      }}
                    />
                    <div class="skill-field-helper">Used internally to reference this setting</div>
                  </div>
                  <div>
                    <label class="skill-setting-label">Placeholder</label>
                    <input type="text" class="skill-setting-input" placeholder="Hint text shown in the input"
                      .value=${entry.placeholder || ""}
                      @input=${(e: Event) => this.updateSettingEntry(idx, { placeholder: (e.target as HTMLInputElement).value })}
                    />
                  </div>
                  <div>
                    <label class="skill-setting-label">Help URL</label>
                    <input type="text" class="skill-setting-input" placeholder="https://docs.example.com/api-key"
                      .value=${entry.help_url || ""}
                      @input=${(e: Event) => this.updateSettingEntry(idx, { help_url: (e.target as HTMLInputElement).value })}
                    />
                    <div class="skill-field-helper">Link to instructions for getting this value</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
      })}

      <button class="btn btn--ghost btn--sm" @click=${() => this.addSettingEntry()}>+ Add Setting</button>
    `;
  }

  // ── Tab: More Options ──

  private renderTabAdvanced(): TemplateResult {
    const checkSvg = html`<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>`;

    return html`
      <!-- Capabilities -->
      <div class="skill-setting-field">
        <label class="skill-setting-label">Capabilities</label>
        <div class="skill-field-helper skill-field-helper--bottom-md">
          Select what your skill needs to work. Users without these capabilities won't be able to use this skill.
        </div>
        <div class="skill-tools-grid">
          ${AVAILABLE_TOOLS.map((tool) => {
            const isSelected = this.createForm.required_tools.includes(tool.id);
            return html`
              <label class="skill-tool-option ${isSelected ? "skill-tool-option--selected" : ""}">
                <input type="checkbox"
                  .checked=${isSelected}
                  @change=${(e: Event) => {
                    const checked = (e.target as HTMLInputElement).checked;
                    const tools = checked
                      ? [...this.createForm.required_tools, tool.id]
                      : this.createForm.required_tools.filter((t) => t !== tool.id);
                    this.createForm = { ...this.createForm, required_tools: tools };
                  }}
                />
                <div class="skill-tool-option__check">${isSelected ? checkSvg : nothing}</div>
                <div>
                  <div class="skill-tool-option__label">${tool.label}</div>
                  <div class="skill-tool-option__desc">${tool.desc}</div>
                </div>
              </label>
            `;
          })}
        </div>
      </div>

      <!-- Slash Commands -->
      <div class="skill-setting-field">
        <label class="skill-setting-label">Slash Commands</label>
        <div class="skill-field-helper skill-field-helper--bottom">
          Commands users can type to quickly trigger this skill (e.g. /weather)
        </div>
        ${this.createCommands.map((cmd, idx) => html`
          <div class="skill-create-command-row">
            <input type="text" class="skill-setting-input skill-flex-1" placeholder="command-name"
              .value=${cmd.name}
              @input=${(e: Event) => {
                const cmds = [...this.createCommands];
                cmds[idx] = { ...cmds[idx], name: (e.target as HTMLInputElement).value };
                this.createCommands = cmds;
              }}
            />
            <input type="text" class="skill-setting-input skill-flex-2" placeholder="Text to prepend when triggered"
              .value=${cmd.prepend_prompt}
              @input=${(e: Event) => {
                const cmds = [...this.createCommands];
                cmds[idx] = { ...cmds[idx], prepend_prompt: (e.target as HTMLInputElement).value };
                this.createCommands = cmds;
              }}
            />
            <button class="skill-delete-btn" title="Remove command" @click=${() => this.removeCommand(idx)}>${this.trashIcon}</button>
          </div>
        `)}
        <button class="btn btn--ghost btn--sm" @click=${() => this.addCommand()}>+ Add Command</button>
      </div>
    `;
  }

  // ── Skill Detail Modal ──

  private renderDetailModal(skill: Skill): TemplateResult {
    const isOwn = skill.user_id === this.currentUserId;
    const installedIds = new Set(this.installed.map((iv) => iv.userSkill.skill_id));
    const isInstalled = installedIds.has(skill.id);
    const userVote = this.userVotes.get(skill.id) ?? 0;
    const hasCommands = skill.commands && skill.commands.length > 0;

    return html`
      <div class="skill-wizard-backdrop" @click=${() => this.closeDetail()}>
        <div class="skill-wizard skill-wizard--wide" @click=${(e: Event) => e.stopPropagation()}>
          <div class="skill-wizard__header">
            ${renderSkillIcon(skill)}
            <div style="flex:1">
              <div class="skill-wizard__title">
                ${skill.name}
                ${skill.is_bundled ? html`<span class="skill-card__badge skill-card__badge--official">Official</span>` : nothing}
              </div>
              <div class="skill-wizard__subtitle">${skill.description}</div>
              <div class="skill-card__meta" style="margin-top:var(--space-2)">
                <span class="skill-card__category">${skill.category}</span>
                <span class="skill-card__stat">by ${skill.author}</span>
                ${(skill.install_count ?? 0) > 0 ? html`<span class="skill-card__stat">${skill.install_count} installs</span>` : nothing}
              </div>
            </div>
            <div style="display:flex;align-items:center;gap:var(--space-3)">
              <!-- Vote -->
              <div class="skill-vote">
                <button class="skill-vote__btn ${userVote === 1 ? "skill-vote__btn--active" : ""}"
                  @click=${() => this.vote(skill.id, 1)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="${userVote === 1 ? "currentColor" : "none"}" stroke="currentColor" stroke-width="2"><path d="M12 5l-8 9h5v6h6v-6h5z"/></svg>
                </button>
                <span class="skill-vote__score">${skill.vote_score ?? 0}</span>
                <button class="skill-vote__btn ${userVote === -1 ? "skill-vote__btn--active-down" : ""}"
                  @click=${() => this.vote(skill.id, -1)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="${userVote === -1 ? "currentColor" : "none"}" stroke="currentColor" stroke-width="2"><path d="M12 19l8-9h-5V4H9v6H4z"/></svg>
                </button>
              </div>
              ${isInstalled
                ? html`<span class="skill-card__installed-badge">Installed</span>`
                : isOwn ? nothing
                : html`<button class="btn btn--primary btn--sm" @click=${() => this.installSkill(skill.id)}>Install</button>`}
            </div>
          </div>

          <div class="skill-wizard__body">
            <!-- Rating summary -->
            ${(skill.review_count ?? 0) > 0
              ? html`<div class="skill-detail-rating">
                  ${renderStars(skill.average_rating ?? 0)}
                  <span class="skill-detail-rating__text">${skill.average_rating} out of 5 (${skill.review_count} ${(skill.review_count ?? 0) === 1 ? "review" : "reviews"})</span>
                </div>`
              : html`<div class="skill-detail-rating"><span class="skill-detail-rating__text">No reviews yet</span></div>`}

            <!-- Commands -->
            ${hasCommands
              ? html`<div class="skill-detail-section">
                  <div class="skill-detail-section__title">Commands</div>
                  <div class="skill-card__commands">
                    ${skill.commands.map((cmd) => html`
                      <span class="skill-card__command">/${cmd.name}</span>
                      <span class="skill-card__stat">${cmd.description}</span>
                    `)}
                  </div>
                </div>`
              : nothing}

            <!-- Required tools -->
            ${skill.required_tools.length > 0
              ? html`<div class="skill-detail-section">
                  <div class="skill-detail-section__title">Required Tools</div>
                  <div class="skill-card__commands">${skill.required_tools.map((t) => html`<span class="skill-card__badge">${t}</span>`)}</div>
                </div>`
              : nothing}

            <!-- Reviews -->
            <div class="skill-detail-section">
              <div class="skill-detail-section__title">Reviews</div>

              ${!isOwn ? html`
                <!-- Review form -->
                <div class="skill-review-form">
                  <div class="skill-review-form__stars">
                    ${[1,2,3,4,5].map((star) => html`
                      <button class="skill-review-star ${this.reviewRating >= star ? "skill-review-star--active" : ""}"
                        @click=${() => { this.reviewRating = star; }}>
                        <svg width="20" height="20" viewBox="0 0 24 24"
                          fill="${this.reviewRating >= star ? "var(--yellow, #eab308)" : "none"}"
                          stroke="${this.reviewRating >= star ? "var(--yellow, #eab308)" : "var(--text-tertiary)"}"
                          stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      </button>
                    `)}
                  </div>
                  <input type="text" class="skill-setting-input" placeholder="Review title (optional)"
                    .value=${this.reviewTitle}
                    @input=${(e: Event) => { this.reviewTitle = (e.target as HTMLInputElement).value; }}
                  />
                  <textarea class="skill-setting-input" rows="3" placeholder="Write your review..."
                    .value=${this.reviewBody}
                    @input=${(e: Event) => { this.reviewBody = (e.target as HTMLTextAreaElement).value; }}
                  ></textarea>
                  <button class="btn btn--primary btn--sm" ?disabled=${this.reviewRating < 1 || this.reviewSaving}
                    @click=${() => this.submitReview()}>
                    ${this.reviewSaving ? "Submitting..." : "Submit Review"}
                  </button>
                </div>
              ` : nothing}

              ${this.detailReviewsLoading
                ? html`<div class="skills-loading"><span class="spinner spinner--sm"></span> Loading reviews...</div>`
                : this.detailReviews.length === 0
                  ? html`<div class="skill-review-empty">No reviews yet. Be the first!</div>`
                  : html`
                      ${this.detailReviews.map((review) => html`
                        <div class="skill-review">
                          <div class="skill-review__header">
                            ${renderStars(review.rating)}
                            ${review.title ? html`<span class="skill-review__title">${review.title}</span>` : nothing}
                            <span class="skill-review__date">${new Date(review.created_at).toLocaleDateString()}</span>
                            ${review.user_id === this.currentUserId
                              ? html`<button class="btn btn--ghost btn--sm btn--danger" @click=${() => this.deleteReview(skill.id)}>Delete</button>`
                              : nothing}
                          </div>
                          ${review.body ? html`<div class="skill-review__body">${review.body}</div>` : nothing}
                        </div>
                      `)}
                      ${this.detailReviewsTotal > this.detailReviews.length
                        ? html`<button class="btn btn--ghost btn--sm" @click=${() => { this.detailReviewsPage++; this.loadReviews(); }}>Load more</button>`
                        : nothing}
                    `}
            </div>
          </div>

          <div class="skill-wizard__footer">
            <button class="btn btn--ghost" @click=${() => this.closeDetail()}>Close</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-skills-view": KatanoidSkillsView;
  }
}
